import { BarLayer, ChartFrame, Legend, sumBuckets, valueDomain, ZeroLine } from '@finsim/charts';
import type { Scenario } from '@finsim/model';
import { useUnit } from 'effector-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScenarioResult } from '../../../entities/simulation';
import { $hiddenScenarios, scenarioVisibilityToggled } from '../../../entities/view-settings';
import { ChartCard, type TableSpec } from './chart-card';
import { FanChart } from './fan-chart';
import { useFanTable } from './fan-table';
import { KpiStrip } from './kpi-strip';
import { NoUncertaintyNote } from './mc-note';
import { scenarioColor } from '../lib/series';
import { SeriesChart } from './series-chart';
import type { CanvasData } from './use-canvas';

const INCOME = 'var(--chart-diverging-pos)';
const EXPENSES = 'var(--chart-diverging-neg)';
const netWorthOf = (r: ScenarioResult) => r.series.netWorth;
const cashOf = (r: ScenarioResult) => r.series.cash;

export function Overview({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const [hidden, toggle] = useUnit([$hiddenScenarios, scenarioVisibilityToggled]);
  const [hover, setHover] = useState(-1);
  const { scenarios, result, valueMode, buckets, fmt } = data;

  const legend = (
    <Legend
      label={t('charts.legend')}
      items={scenarios.map((s) => ({
        id: s.id,
        label: s.name,
        color: scenarioColor(s),
        hidden: hidden.has(s.id),
      }))}
      onToggle={(id) => {
        toggle(id as Scenario['id']);
      }}
    />
  );

  const yearTable = (metric: 'netWorth' | 'cash', title: string): TableSpec => ({
    caption: title,
    columns: [t('charts.year'), ...data.visible.map((s) => s.name)],
    rows: buckets.map((b) => ({
      label: String(b.year),
      values: data.visible.map((s) =>
        fmt.money(result.scenarios[s.id]?.series[metric][valueMode][b.to] ?? 0),
      ),
    })),
  });

  const mc = data.mc !== null;
  const mcCaption = [t('mc.bandsCaption'), data.moneyCaption].filter(Boolean).join(' · ');
  const netWorthFan = useFanTable(data, 'netWorth', data.visible, t('charts.netWorth'));
  const cashFan = useFanTable(data, 'cash', data.visible, t('charts.cash'));

  const focused = data.focusedResult;
  const yearly = useMemo(
    () =>
      focused
        ? {
            income: sumBuckets(focused.series.income[valueMode], buckets),
            expenses: sumBuckets(focused.series.expenses[valueMode], buckets).map((v) => -v),
          }
        : null,
    [focused, valueMode, buckets],
  );

  return (
    <div className="space-y-4 p-4">
      <NoUncertaintyNote data={data} />
      <KpiStrip data={data} />
      <ChartCard
        title={t('charts.netWorth')}
        subtitle={mc ? mcCaption : data.moneyCaption}
        legend={legend}
        table={mc ? netWorthFan : yearTable('netWorth', t('charts.netWorth'))}
      >
        {mc ? (
          <FanChart
            data={data}
            title={t('charts.netWorth')}
            metric="netWorth"
            scenarios={data.visible}
            height={280}
            hover={hover}
            onHover={setHover}
          />
        ) : (
          <SeriesChart
            data={data}
            title={t('charts.netWorth')}
            select={netWorthOf}
            height={280}
            hover={hover}
            onHover={setHover}
            showEvents
          />
        )}
      </ChartCard>
      <ChartCard
        title={t('charts.cash')}
        subtitle={mc ? mcCaption : data.moneyCaption}
        table={mc ? cashFan : yearTable('cash', t('charts.cash'))}
      >
        {mc ? (
          <FanChart
            data={data}
            title={t('charts.cashAria')}
            metric="cash"
            scenarios={data.visible}
            height={200}
            hover={hover}
            onHover={setHover}
          />
        ) : (
          <SeriesChart
            data={data}
            title={t('charts.cashAria')}
            select={cashOf}
            height={200}
            hover={hover}
            onHover={setHover}
            shadeNegative
          />
        )}
      </ChartCard>
      {yearly && data.focused && (
        <ChartCard
          title={t('charts.incomeVsExpenses')}
          subtitle={[data.focused.name, mc ? t('mc.averagePath') : undefined, data.moneyCaption]
            .filter(Boolean)
            .join(' · ')}
          legend={
            <Legend
              label={t('charts.incomeVsExpenses')}
              items={[
                { id: 'income', label: t('charts.income'), color: INCOME },
                { id: 'expenses', label: t('charts.expenses'), color: EXPENSES },
              ]}
            />
          }
          table={{
            caption: t('charts.incomeVsExpenses'),
            columns: [t('charts.year'), t('charts.income'), t('charts.expenses')],
            rows: buckets.map((b, i) => ({
              label: String(b.year),
              values: [fmt.money(yearly.income[i] ?? 0), fmt.money(-(yearly.expenses[i] ?? 0))],
            })),
          }}
        >
          <ChartFrame
            markers={data.markers}
            dates={data.dates}
            range={data.range}
            height={200}
            yDomain={valueDomain([yearly.income, yearly.expenses])}
            formatY={fmt.compact}
            formatXTick={fmt.year}
            ariaLabel={t('charts.incomeVsExpensesAria', { name: data.focused.name })}
            renderTooltip={(i) => {
              const b = buckets.findIndex((x) => i >= x.from && i <= x.to);
              const bucket = buckets[b];
              if (!bucket) return null;
              return (
                <div className="space-y-1">
                  <p className="text-text-3">
                    {bucket.partial ? t('charts.partialYear', { year: bucket.year }) : bucket.year}
                  </p>
                  <p className="flex justify-between gap-4">
                    <span className="text-text-2">{t('charts.income')}</span>
                    <span className="tabular font-medium">{fmt.money(yearly.income[b] ?? 0)}</span>
                  </p>
                  <p className="flex justify-between gap-4">
                    <span className="text-text-2">{t('charts.expenses')}</span>
                    <span className="tabular font-medium">
                      {fmt.money(-(yearly.expenses[b] ?? 0))}
                    </span>
                  </p>
                </div>
              );
            }}
          >
            <ZeroLine />
            <BarLayer
              buckets={buckets}
              mode="grouped"
              series={[
                { id: 'income', values: yearly.income, color: INCOME },
                { id: 'expenses', values: yearly.expenses, color: EXPENSES },
              ]}
            />
          </ChartFrame>
        </ChartCard>
      )}
    </div>
  );
}
